import {
  ActionResponse,
  ParseJsonResponse,
  StepDto,
  TemplateTypeEnum,
  WorkflowDefinitionDto,
} from 'workflow-definition/openapi';
import { ActionSettingTypeCodes, TriggerDropdownOptions } from '../models';
import { parseConditionals } from './workflow-mutations';
import { flatParsedJsonWithPath } from './parsedJson';

export const getSegmentFromWorkflowById = (
  workflowObject: WorkflowDefinitionDto | undefined,
  segmentId: number
) => {
  if (!workflowObject) return undefined;
  if (segmentId === undefined || segmentId === null) return undefined;

  const segment = workflowObject.segments?.find(s => s.id === segmentId);
  if (!segment) return null;
  return segment;
};

export const getStepFromSegmentFromWorkflowById = async (
  workflowObject: WorkflowDefinitionDto,
  segmentId: number,
  stepId: number
) => {
  if (!workflowObject) return undefined;
  if (segmentId === undefined || segmentId === null) return undefined;
  if (stepId === undefined || stepId === null) return undefined;

  const segment = workflowObject.segments?.find(s => s.id === segmentId);
  if (!segment) return undefined;

  const step = segment.steps?.find(s => s.id === stepId);
  if (!step) return undefined;
  return step;
};

export const getActionFromStepFromSegmentFromWorkflowById = (
  workflowObject: WorkflowDefinitionDto,
  segmentId: number,
  stepId: number,
  actionId: number
) => {
  if (!workflowObject) return undefined;
  if (segmentId === undefined || segmentId === null || segmentId === -1)
    return undefined;
  if (stepId === undefined || stepId === null || stepId === -1)
    return undefined;
  if (actionId === undefined || actionId === null || actionId === -1)
    return undefined;

  const segment = workflowObject.segments?.find(s => s.id === segmentId);
  if (!segment) throw Error(`Segment (${segmentId}) not found.`);

  const step = segment.steps?.find(s => s.id === stepId);
  if (!step) throw Error(`Step (${stepId}) not found.`);

  const action = step.actions?.find(a => a.id === actionId);
  if (!action) return undefined;

  return action;
};

export const getStepActionSelected = (
  data: ActionResponse,
  parsedJson: ParseJsonResponse,
  currentWorkflow: WorkflowDefinitionDto,
  segmentId: number,
  stepId: number,
  actionId: number
) => {
  if (!parsedJson || !currentWorkflow) return undefined;
  const parseJsonDataFlatted = flatParsedJsonWithPath(parsedJson?.fields || []);

  if (data?.prePopulatedValues && data?.prePopulatedValues?.length > 0) {
    data.prePopulatedValues.forEach(prePopulatedValue => {
      if (prePopulatedValue.type === 'JSON' || !prePopulatedValue.type) {
        prePopulatedValue.value = parseJsonDataFlatted.find(
          json => json.id === prePopulatedValue.jsonFieldId
        )?.path;
      }
    });
  }

  // Ensure properties is always a plain object so downstream code never has to
  // guard against null. Type-specific cases below may still add/adjust keys.
  data.properties = { ...(data.properties ?? {}) };

  switch (data.type) {
    case ActionSettingTypeCodes.ELD_LOCATION:
      // eslint-disable-next-line no-case-declarations
      const eldLocationItem = TriggerDropdownOptions.find(
        o => o.value === data?.properties?.['triggerType']
      );
      if (eldLocationItem) {
        data.properties['value'] = eldLocationItem.label;
      }
      break;

    case ActionSettingTypeCodes.GEOFENCE:
      data.properties['latJsonFieldLabel'] = parseJsonDataFlatted.find(
        json => json.id === Number(data?.properties?.['latJsonFieldId'])
      )?.name;
      data.properties['longJsonFieldLabel'] = parseJsonDataFlatted.find(
        json => json.id === Number(data?.properties?.['longJsonFieldId'])
      )?.name;
      data.properties['zoneJsonFieldLabel'] = parseJsonDataFlatted.find(
        json => json.id === Number(data?.properties?.['zoneJsonFieldId'])
      )?.name;
      break;

    case ActionSettingTypeCodes.CERTIFY_LOGS:
      if (!('type' in data.properties)) data.properties['type'] = '';
      break;

    case ActionSettingTypeCodes.DATETIME:
      if (!('format' in data.properties)) data.properties['format'] = '';
      break;

    case ActionSettingTypeCodes.SIMPLE_BUTTON: {
      const buttonSubtype = data.properties?.['action'];
      const isEmailOrSmsSubtype =
        buttonSubtype === 'EMAILAPP' || buttonSubtype === 'SMSAPP';
      if (data.properties && isEmailOrSmsSubtype) {
        // isPrePopulated stays as the action-level value; recipient pre-populate
        // is tracked separately via the prePopulateRecipient/prePopulateValue properties.
        // Resolve the recipient JSON field label so actionSettingsButton can populate
        // recipientPrepopulate.json without needing another lookup.
        const jsonFieldId = Number(data?.properties?.['prePopulateValue']);
        if (jsonFieldId) {
          data.properties['prePopulateLabel'] = parseJsonDataFlatted.find(
            json => json.id === jsonFieldId
          )?.name;
        }
      }
      break;
    }
    case ActionSettingTypeCodes.TFLO_EMAIL:
    case ActionSettingTypeCodes.TFLO_SMS:
      if (data.properties) {
        data.isPrePopulated =
          data?.properties?.['prePopulateRecipient'] === 'true';
        const jsonFieldId = Number(data?.properties?.['prePopulateValue']);
        if (jsonFieldId) {
          data.prePopulatedValues = [
            { type: 'JSON', jsonFieldId, value: null },
          ];
          data.properties['prePopulateLabel'] = parseJsonDataFlatted.find(
            json => json.id === jsonFieldId
          )?.name;
        }
      }
      break;
  }

  const currentAction = getActionFromStepFromSegmentFromWorkflowById(
    currentWorkflow,
    segmentId,
    stepId,
    actionId
  );

  return {
    ...data,
    conditionals: parseConditionals(currentAction?.conditionals),
    blockers: parseConditionals(currentAction?.blockers),
  };
};

/**
  Given an array/collecton of steps, a starting index in the array,
  the function should find the next step following the index, that does
  not have any conditionals.
  * @param steps - A collection of steps, generally from a Segment.
  * @param startingIndex - The new step response data to be added.
  * @returns the next step that contains no conditons
*/
export const getNextConditionlessStep = (
  steps: StepDto[],
  startingIndex: number
) => {
  return steps.slice(startingIndex).find(s => {
    return !s?.conditionals?.length;
  });
};

/**
  Given an array/collecton of steps, a starting index in the array,
  the function should find the last step before the index, that does
  not have any conditionals.
  * @param steps - A collection of steps, generally from a Segment.
  * @param startingIndex - The new step response data to be added.
  * @returns the next step that contains no conditons
*/
export const getLastConditionlessStep = (
  steps: StepDto[],
  startingIndex: number
) => {
  return steps
    .slice(0, startingIndex)
    .reverse()
    .find(s => {
      return !s?.conditionals?.length;
    });
};

export const getTemplateType = (
  workflow:
    | {
        isTemplate?: boolean;
        customerId?: string | null;
        division?: string | null;
      }
    | null
    | undefined
): TemplateTypeEnum => {
  if (!workflow?.isTemplate) return TemplateTypeEnum.None;
  if (workflow.customerId && workflow.division) return TemplateTypeEnum.Regular;
  if (workflow.customerId && !workflow.division)
    return TemplateTypeEnum.Customer;
  if (!workflow.customerId && workflow.division)
    return TemplateTypeEnum.Division;
  return TemplateTypeEnum.Global;
};
